import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { API } from '../../../API';
import { Loader } from '../../../components/Loader/Loader';
import {
  ExecutionHistoryResponse,
  ExecutionStatus,
  isTerminalStatus,
} from '../types';
import { CancelOutcome, CancelRunButton } from './CancelRunButton';
import { StatusBadge } from './ResultDisplay';

import styles from './RunHistory.module.scss';

interface RunHistoryProps {
  appId: string;
  /** Execution currently shown on the page (from the `execution` URL param). */
  activeExecutionId: string | null;
  /** Live status of that execution, so the row updates while it is polling. */
  activeStatus: ExecutionStatus | null;
  onSelect: (executionId: string) => void;
  /** Reports a cancel attempt so the page can re-sync the run it is showing. */
  onCancelSettled: (executionId: string, outcome: CancelOutcome) => void;
}

const PAGE_SIZE = 20;

function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  return date.toLocaleString();
}

export const RunHistory: React.FC<RunHistoryProps> = ({
  appId,
  activeExecutionId,
  activeStatus,
  onSelect,
  onCancelSettled,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<ExecutionHistoryResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [reloadNonce, setReloadNonce] = useState(0);

  const fetchHistory = useCallback(() => {
    const params = new URLSearchParams();
    params.set('app_id', appId);
    params.set('page', String(page));
    params.set('size', String(PAGE_SIZE));
    API.fetch('GET', `/API/v2/windmill/executions?${params.toString()}`)
      .then((resp: ExecutionHistoryResponse) => {
        setData(resp);
        setLoadError(null);
      })
      .catch((err: Error) => {
        setLoadError(err.message);
      });
  }, [appId, page]);

  const fetchHistoryRef = useRef(fetchHistory);
  useEffect(() => {
    fetchHistoryRef.current = fetchHistory;
  }, [fetchHistory]);

  // Reload on paging, and also when a run is opened or reaches a terminal
  // state, so a run that just started/finished shows up without the user
  // losing the page they are on.
  const activeIsTerminal = activeStatus !== null && isTerminalStatus(activeStatus);
  const refreshKey = `${appId}|${page}|${activeExecutionId ?? ''}|${activeIsTerminal}|${reloadNonce}`;
  const lastRefreshKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastRefreshKeyRef.current === refreshKey) return;
    lastRefreshKeyRef.current = refreshKey;
    fetchHistoryRef.current();
  }, [refreshKey]);

  const handleCancelSettled = useCallback(
    (executionId: string, outcome: CancelOutcome) => {
      // The row's status changed either way (cancelled, or it had already
      // finished), so pull the list again.
      setReloadNonce((n) => n + 1);
      onCancelSettled(executionId, outcome);
    },
    [onCancelSettled],
  );

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.size)) : 1;

  if (!data && !loadError) {
    return (
      <section className={styles.root}>
        <h2 className={styles.title}>{t('Run history')}</h2>
        <Loader />
      </section>
    );
  }

  return (
    <section className={styles.root}>
      <h2 className={styles.title}>{t('Run history')}</h2>

      {loadError && <div className={styles.error}>{loadError}</div>}

      {data && data.objects.length === 0 && (
        <div className={styles.empty}>{t('No executions yet for this app.')}</div>
      )}

      {data && data.objects.length > 0 && (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">{t('Status')}</th>
                  <th scope="col">{t('Triggered by')}</th>
                  <th scope="col">{t('Started')}</th>
                  <th scope="col">{t('Completed')}</th>
                  <th scope="col">{t('Duration')}</th>
                  <th scope="col">{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.objects.map((execution) => {
                  const isActive = execution.id === activeExecutionId;
                  const status =
                    isActive && activeStatus ? activeStatus : execution.status;

                  return (
                    <tr
                      key={execution.id}
                      className={`${styles.row} ${isActive ? styles.activeRow : ''}`}
                      onClick={() => onSelect(execution.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSelect(execution.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-current={isActive ? 'true' : undefined}
                      aria-label={
                        t('Open execution details for {{id}}', {
                          id: execution.id,
                        }) as string
                      }
                    >
                      <td>
                        <StatusBadge status={status} />
                      </td>
                      <td>
                        {execution.triggered_by_user_name ??
                          `#${execution.triggered_by_user_id}`}
                      </td>
                      <td className={styles.dim}>
                        {formatTimestamp(execution.started_at ?? execution.created_at)}
                      </td>
                      <td className={styles.dim}>
                        {formatTimestamp(execution.completed_at)}
                      </td>
                      <td className={styles.dim}>
                        {formatDuration(execution.duration_ms)}
                      </td>
                      <td>
                        {!isTerminalStatus(status) && (
                          <CancelRunButton
                            executionId={execution.id}
                            onSettled={handleCancelSettled}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data.total > data.size && (
            <div className={styles.pagination}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('Previous')}
              </button>
              <span>
                {t('Page {{page}} of {{total}}', {
                  page: data.page,
                  total: totalPages,
                })}
              </span>
              <button
                type="button"
                disabled={data.page * data.size >= data.total}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('Next')}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};
